import { RoseMark } from "@/components/RoseMark";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, HeartHandshake, ShieldCheck, LineChart, FlaskConical, Stethoscope } from "lucide-react";
import { useDemo } from "@/hooks/useDemo";

export default function Landing() {
  const demo = useDemo();
  return (
    <div className="grain relative">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-10 sm:pt-16 pb-20 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[var(--brand-teal)] bg-[#fefaf4] ring-1 ring-stone-200 px-3 py-1.5 rounded-full">
              <RoseMark className="w-5 h-5" /> Root &amp; Rose · for tending your habit garden
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 mt-5 leading-[1.05]">
              Start the good.<br />
              Ease the rest.<br />
              <span className="text-amber-700 italic">Only celebrated.</span>
            </h1>
            <p className="text-base sm:text-lg text-stone-600 mt-6 max-w-xl leading-relaxed">
              Root & Rose helps you begin or unwind a habit with AI-crafted rewards
              made for you — and, if you like, a friend or coach who quietly cheers you on.{" "}
              <span className="font-semibold text-stone-800">
                No streaks broken. No shaming. Ever.
              </span>
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => demo.start("client")} disabled={demo.busy} data-testid="landing-try-demo" className="amber-btn text-base disabled:opacity-60">
                <FlaskConical className="w-4 h-4 mr-2 -ml-1" /> {demo.busy ? "Planting your demo garden…" : "Try the demo"}
              </button>
              <Link to="/register" data-testid="landing-cta-primary" className="inline-flex items-center px-5 py-2.5 rounded-full text-base font-semibold text-white bg-stone-900 hover:bg-stone-700 transition-colors">
                Create account
              </Link>
              <Link
                to="/login"
                data-testid="landing-cta-secondary"
                className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-stone-800 border border-stone-300 hover:bg-stone-900 hover:text-white transition-colors"
              >
                I already have an account
              </Link>
            </div>
            <p className="mt-3 text-xs text-stone-500" data-testid="landing-demo-note">
              No sign-up needed. The demo comes with sample habits and six weeks of pretend check-ins; it resets after 24 hours.{" "}
              <button onClick={() => demo.start("clinician")} disabled={demo.busy} data-testid="landing-try-clinician-demo" className="inline-flex items-center gap-1 font-semibold text-amber-800 hover:underline disabled:opacity-60">
                <Stethoscope className="w-3.5 h-3.5" /> Coach? Peek at a sample caseload
              </button>
            </p>

            <div className="mt-10 flex items-center gap-6 text-xs text-stone-500">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                HIPAA-aligned security
              </div>
              <div className="flex items-center gap-1.5">
                <HeartHandshake className="w-4 h-4 text-amber-700" />
                Positive-only feedback
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-amber-200/40 via-orange-200/30 to-emerald-200/40 blur-3xl rounded-full" />
              <img
                src="https://images.unsplash.com/photo-1727822565830-35bdc8597e84?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Warm wellness moment"
                className="relative rounded-3xl shadow-2xl w-full h-[440px] object-cover border-4 border-white"
              />
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-6 -left-6 warm-card p-4 max-w-[240px]"
              >
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-800 font-bold">
                      Reward earned
                    </div>
                    <div className="text-sm font-semibold text-stone-800">
                      Sunset walk, on you.
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Sparkles,
              title: "Rewards, made for you",
              desc: "Claude Sonnet 5 crafts personal, non-material rewards tuned to your habit and interests.",
              tint: "bg-amber-100 text-amber-700",
            },
            {
              icon: HeartHandshake,
              title: "No punishment. Ever.",
              desc: "Miss a day? We say nothing. Show up? We celebrate. Rest is part of growth.",
              tint: "bg-orange-100 text-orange-700",
            },
            {
              icon: LineChart,
              title: "Your coach sees the pattern",
              desc: "Choose what a friend or coach can see — the garden, your habits, or everything — and change it any time.",
              tint: "bg-emerald-100 text-emerald-700",
            },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="warm-card p-7"
            >
              <div className={`w-11 h-11 rounded-xl ${f.tint} flex items-center justify-center mb-4`}>
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl font-bold text-stone-900 mb-2">{f.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
